import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { FamilySelector, useFamilySelection } from '../common';

interface ListItemData {
  id: string;
  text: string;
  completed: boolean;
}

interface ListData {
  title: string;
  items: ListItemData[];
}

interface ListFormDialogProps {
  open: boolean;
  onClose: () => void;
  // onSave accepts familyIds array for multi-family support
  onSave: (data: ListData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  list?: { id: string; family_id?: string; data?: { data?: ListData } } | null;
}

export const ListFormDialog: React.FC<ListFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  list,
}) => {
  const isEditing = !!list;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection(list?.family_id ? [list.family_id] : undefined);

  const [title, setTitle] = useState('');
  const [items, setItems] = useState<ListItemData[]>([]);
  const [newItemText, setNewItemText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (list) {
        const data = (list.data?.data || {}) as ListData;
        setTitle(data.title || '');
        setItems(data.items || []);
      } else {
        setTitle('');
        setItems([]);
      }
      setNewItemText('');
      setError(null);
    }
  }, [open, list]);

  const handleAddItem = () => {
    if (!newItemText.trim()) return;

    setItems((prev) => [
      ...prev,
      {
        id: `item_${Date.now()}`,
        text: newItemText.trim(),
        completed: false,
      },
    ]);
    setNewItemText('');
  };

  const handleToggleItem = (itemId: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, completed: !item.completed } : item
      )
    );
  };

  const handleRemoveItem = (itemId: string) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const handleUpdateItem = (itemId: string, text: string) => {
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, text } : item))
    );
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (selectedFamilyIds.length === 0) {
      setError('Please select at least one family');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(
        {
          title: title.trim(),
          items,
        },
        selectedFamilyIds
      );
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save list');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete list');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit List' : 'New List'}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          label="List Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          autoFocus
          sx={{ mb: 2 }}
        />

        {/* Family Selector - only shows if user belongs to multiple families */}
        {showFamilySelector && !isEditing && (
          <Box sx={{ mb: 2 }}>
            <FamilySelector
              value={selectedFamilyIds}
              onChange={setSelectedFamilyIds}
            />
          </Box>
        )}

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Items
        </Typography>

        {/* Add new item */}
        <TextField
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Add an item..."
          fullWidth
          size="small"
          sx={{ mb: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleAddItem} size="small" disabled={!newItemText.trim()}>
                  <AddIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* List items */}
        <List disablePadding sx={{ maxHeight: 300, overflow: 'auto' }}>
          {items.map((item) => (
            <ListItem
              key={item.id}
              dense
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                mb: 0.5,
                '&:hover': { bgcolor: 'action.hover' },
              }}
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleRemoveItem(item.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  checked={item.completed}
                  onChange={() => handleToggleItem(item.id)}
                  size="small"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <TextField
                    value={item.text}
                    onChange={(e) => handleUpdateItem(item.id, e.target.value)}
                    variant="standard"
                    fullWidth
                    sx={{
                      '& .MuiInput-root': {
                        textDecoration: item.completed ? 'line-through' : 'none',
                        color: item.completed ? 'text.secondary' : 'text.primary',
                      },
                    }}
                    InputProps={{ disableUnderline: true }}
                  />
                }
              />
            </ListItem>
          ))}
        </List>

        {items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No items yet. Add your first item above.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button
            onClick={handleDelete}
            color="error"
            startIcon={<DeleteIcon />}
            disabled={saving}
            sx={{ mr: 'auto' }}
          >
            Delete
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ListFormDialog;
